import { InfoPage } from '@/components/info-page';
import { LinkToExternal } from '@/components/link-to-external';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Button } from '@/components/ui/button';
import { EmptyArray, getLessons } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { usePaginatedFilters } from '@/lib/filtering';
import { paginationRequestSchema, toStringPagination } from '@/lib/pagination';
import { validateRouteRole } from '@/lib/routing';
import { CreateLessons } from '@/routes/_layout/lessons/-components/create-lessons';
import { CreateLessonsByCsv } from '@/routes/_layout/lessons/-components/create-lessons-by-csv';
import { LearningObjectivesCell } from '@/routes/_layout/lessons/-components/learning-objectives-cell';
import { isAirAdmin } from '@airreading/common';
import { LessonWithAssessments } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { z } from 'zod';
import { lessonsSectionTabs } from './-components/tabs';

const curriculumsSearchSchema = paginationRequestSchema.and(
  z.object({
    query: z.string().optional().catch(''),
  }),
);

export const Route = createFileRoute('/_layout/lessons/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: CurriculumsIndex,
  validateSearch: curriculumsSearchSchema,
});

const columnHelper = createColumnHelper<LessonWithAssessments>();
const columns = [
  columnHelper.accessor('name', {
    id: 'name',
    header: 'Lesson name',
    cell: ({ row, getValue }) => {
      return (
        <Link to="/lessons/$lessonId" params={{ lessonId: row.original.id }}>
          {getValue()}
        </Link>
      );
    },
  }),
  columnHelper.accessor('objectives', {
    id: 'objectives',
    header: 'Learning objectives',
    cell: ({ getValue }) => (
      <div>
        <LearningObjectivesCell objectives={getValue() || []} />
      </div>
    ),
  }),
  columnHelper.display({
    id: 'lesson-link',
    header: 'Lesson slides',
    cell: ({ row }) => {
      const { lessonLinkKTo2, lessonLink3To5, lessonLink6To8 } = row.original;
      if (!lessonLinkKTo2 && !lessonLink3To5 && !lessonLink6To8) {
        return 'No class slides yet';
      }
      return (
        <div className="flex flex-col">
          {lessonLinkKTo2 && <LinkToExternal href={lessonLinkKTo2}>Lesson slides for K-2</LinkToExternal>}
          {lessonLink3To5 && <LinkToExternal href={lessonLink3To5}>Lesson slides for 3-5</LinkToExternal>}
          {lessonLink6To8 && <LinkToExternal href={lessonLink6To8}>Lesson slides for 6-8</LinkToExternal>}
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'handout-link',
    header: 'Lesson handouts',
    cell: ({ row }) => {
      const links = row.original.handouts;
      if (!links?.length) {
        return 'No class handout';
      }
      return links.map((href) => (
        <div key={href}>
          <LinkToExternal href={href}>Handout</LinkToExternal>
        </div>
      ));
    },
  }),
  columnHelper.accessor('assessments', {
    id: 'assessments',
    header: 'Assessments',
    cell: ({ getValue }) => {
      return <div>{getValue()?.map((assessment) => <div key={assessment.id}>{assessment.name}</div>)}</div>;
    },
  }),
];

function CurriculumsIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[lessonName, activeLessonName], setLessonName],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query']);

  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(getLessons({ ...toStringPagination({ pageIndex, pageSize }), query: activeLessonName }));
  const lessons = useMemo(() => response?.lessons || EmptyArray, [response]);
  useErrorToast(error, 'Error loading curriculums. Please try again');

  const table = useReactTable({
    data: lessons,
    columns,
    rowCount: response?.total || 0,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    defaultColumn: {
      enableSorting: false,
    },
  });

  return (
    <InfoPage
      headerText="Lessons"
      headerActions={
        <>
          <CreateLessons />
          <CreateLessonsByCsv trigger={<Button>Create lessons by CSV</Button>} />
        </>
      }
      tabs={lessonsSectionTabs()}
    >
      <EntityTable
        table={table}
        showPagination={true}
        isLoading={isPending}
        emptyState={'No lessons found'}
        errorState={<TableErrorState refetchAction={refetch} />}
        hasError={!!error}
        filters={{
          filterLeftComponents: (
            <>
              <TableFilterInputField
                placeholder="Search lessons by name"
                value={lessonName ?? ''}
                onChange={(event) => {
                  setLessonName(event.target.value);
                }}
              />
              <TableFilterResetButton show={true} onClick={resetFilters} />
            </>
          ),
        }}
      />
    </InfoPage>
  );
}
